
import { useState, useEffect } from 'react';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';
import { useActivation } from '@/contexts/ActivationContext';

interface RemoteDoc {
  id: string;
  documentId?: string;
  name: string;
  hint: string;
  status: 'not_uploaded' | 'reviewing' | 'approved';
  uploadedAt: string | null;
  fileName: string | null;
  filename?: string | null;
}

interface DocumentItem extends RemoteDoc {
  file: File | null;
  uploading: boolean;
  uploadError: string | null;
}

interface Signer {
  id: string;
  name: string;
  email: string | null;
}

function DocumentStatusBadge({ status }: { status: DocumentItem['status'] }) {
  if (status === 'not_uploaded') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300">
        No cargado
      </span>
    );
  }
  if (status === 'reviewing') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-1.5" />
        Cargado - En revisión
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5" />
      Aprobado
    </span>
  );
}

const EMPTY_DOC_FIELDS = { status: 'not_uploaded' as const, uploadedAt: null, fileName: null, file: null, uploading: false, uploadError: null };

const FALLBACK_DOCS_EMPRESA: DocumentItem[] = [
  { id: 'constitucion', name: 'Constitución de sociedad', hint: 'PDF, DOCX o JPG (MAX. 10MB)', ...EMPTY_DOC_FIELDS },
  { id: 'poderes', name: 'Poderes vigentes (máximo 60 días)', hint: 'El documento no debe tener más de 60 días de antigüedad', ...EMPTY_DOC_FIELDS },
];

const FALLBACK_DOCS_PERSONA_NATURAL: DocumentItem[] = [
  { id: 'identidad', name: 'RUT / Carnet de Identidad (ambos lados)', hint: 'PDF (MAX. 10MB) — un solo archivo con ambos lados del carnet', ...EMPTY_DOC_FIELDS },
];

function getFallbackDocs(companyType: string | null): DocumentItem[] {
  if (companyType === 'persona_natural') return FALLBACK_DOCS_PERSONA_NATURAL;
  if (companyType === 'empresa') return FALLBACK_DOCS_EMPRESA;
  return [];
}

export default function FirmaPagarePanel() {
  const { isStepComplete } = useActivation();
  const isCompleted = isStepComplete('firmaPagare');

  const [signers, setSigners] = useState<Signer[]>([]);
  const [loadingSigners, setLoadingSigners] = useState(true);
  const [companyType, setCompanyType] = useState<string | null>(null);
  const [docs, setDocs] = useState<DocumentItem[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [downloadingDoc, setDownloadingDoc] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const [savingType, setSavingType] = useState(false);

  const [signerEmails, setSignerEmails] = useState<Record<string, string>>({});
  const [savingEmails, setSavingEmails] = useState(false);
  const [emailSaveError, setEmailSaveError] = useState<string | null>(null);
  const [dragOverDocId, setDragOverDocId] = useState<string | null>(null);

  const hasUploadedDocs = docs.some((d) => d.status !== 'not_uploaded');

  const handleSelectType = async (type: 'empresa' | 'persona_natural') => {
    if (savingType || hasUploadedDocs || type === companyType) return;
    setSavingType(true);
    try {
      const url = getEndpointUrl('pagare-docs', 'set-type');
      const res = await httpClient.post(url, { companyType: type });
      const data = await res.json() as { companyType: string | null; documentos: RemoteDoc[] };
      setCompanyType(data.companyType);
      const fallbacks = getFallbackDocs(data.companyType);
      const apiMap = new Map(data.documentos.map((d) => [d.documentId ?? d.id, d]));
      setDocs(
        fallbacks.map((fallback) => {
          const remote = apiMap.get(fallback.id);
          if (!remote) return { ...fallback };
          const normalizedFileName = remote.fileName ?? remote.filename ?? null;
          return { ...fallback, ...remote, fileName: normalizedFileName, file: null, uploading: false, uploadError: null };
        })
      );
    } catch (err) {
      console.error('Error al actualizar tipo de negocio:', err);
    } finally {
      setSavingType(false);
    }
  };

  const setDocField = (docId: string, fields: Partial<DocumentItem>) => {
    setDocs((prev) => prev.map((d) => d.id === docId ? { ...d, ...fields } : d));
  };

  const handleFileChange = async (docId: string, file: File | null) => {
    if (!file) {
      setDocField(docId, { file: null, status: 'not_uploaded', uploadError: null });
      return;
    }

    setDocField(docId, { file, uploading: true, uploadError: null });

    try {
      const signedUrlEndpoint = getEndpointUrl('upload', 'file');
      const signedRes = await httpClient.post(signedUrlEndpoint, {
        contentType: file.type || 'application/octet-stream',
      });
      const signedData = await signedRes.json();

      if (!signedData.uploadUuid || !signedData.endpoint) {
        throw new Error('No se recibió una URL válida para la subida del archivo');
      }

      const uploadRes = await fetch(signedData.endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });

      if (!uploadRes.ok) {
        throw new Error(`Error ${uploadRes.status}: No se pudo subir el archivo`);
      }

      const registerEndpoint = getEndpointUrl('pagare-docs', 'upload');
      const registerRes = await httpClient.post(registerEndpoint, {
        documentId: docId,
        uploadUuid: signedData.uploadUuid,
        filename: file.name,
      });
      await registerRes.json();

      setDocField(docId, {
        uploading: false,
        status: 'reviewing',
        fileName: file.name,
        uploadedAt: new Date().toISOString(),
        uploadError: null,
      });
    } catch (err: any) {
      console.error('Error al subir documento:', err);
      setDocField(docId, {
        file: null,
        uploading: false,
        uploadError: err.message || 'No se pudo subir el archivo. Intenta nuevamente.',
      });
    }
  };

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const url = getEndpointUrl('pagare-docs', 'list');
        const data = await httpClient.get<{ companyType: string | null; documentos: RemoteDoc[] }>(url);
        setCompanyType(data.companyType);
        const fallbacks = getFallbackDocs(data.companyType);
        const apiMap = new Map(data.documentos.map((d) => [d.documentId ?? d.id, d]));
        setDocs(
          fallbacks.map((fallback) => {
            const remote = apiMap.get(fallback.id);
            if (!remote) return { ...fallback };
            const normalizedFileName = remote.fileName ?? remote.filename ?? null;
            return { ...fallback, ...remote, fileName: normalizedFileName, file: null, uploading: false, uploadError: null };
          })
        );
      } catch (err) {
        console.error('Error al cargar documentos:', err);
      } finally {
        setLoadingDocs(false);
      }
    };

    const fetchSigners = async () => {
      try {
        const url = getEndpointUrl('pagare-firmantes', 'list');
        const data = await httpClient.get<{ firmantes: Signer[] }>(url);
        setSigners(data.firmantes);
      } catch (err) {
        console.error('Error al cargar firmantes:', err);
      } finally {
        setLoadingSigners(false);
      }
    };

    fetchDocs();
    fetchSigners();
  }, []);

  // Prevenir que el navegador abra archivos al soltarlos fuera de la zona de drop
  useEffect(() => {
    const preventDefaults = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('dragover', preventDefaults);
    document.addEventListener('drop', preventDefaults);
    return () => {
      document.removeEventListener('dragover', preventDefaults);
      document.removeEventListener('drop', preventDefaults);
    };
  }, []);

  const handleDownload = async () => {
    setDownloadingDoc(true);
    setDownloadError(null);
    try {
      const url = getEndpointUrl('pagare-firmado', 'get');
      const data = await httpClient.get<{ url: string; filename?: string }>(url);
      const response = await fetch(data.url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = data.filename || 'pagare-firmado.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error al descargar documento firmado:', err);
      setDownloadError('No se pudo obtener el documento. Intenta nuevamente.');
    } finally {
      setDownloadingDoc(false);
    }
  };

  const handleSaveEmails = async () => {
    const missingEmails = signers.filter((s) => {
      const draft = signerEmails[s.id];
      return !s.email && (!draft || !draft.trim());
    });
    if (missingEmails.length > 0) {
      setEmailSaveError('Todos los firmantes deben tener un correo electrónico asignado.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = signers.filter((s) => {
      const email = signerEmails[s.id]?.trim() || s.email;
      return email && !emailRegex.test(email);
    });
    if (invalidEmails.length > 0) {
      setEmailSaveError('Uno o más correos electrónicos no son válidos.');
      return;
    }

    setSavingEmails(true);
    setEmailSaveError(null);

    try {
      const url = getEndpointUrl('pagare-firmantes', 'update-emails');
      const payload = {
        firmantes: signers.map((s) => ({
          id: s.id,
          email: signerEmails[s.id]?.trim() || s.email,
        })),
      };
      await httpClient.post(url, payload);

      setSigners((prev) =>
        prev.map((s) => ({
          ...s,
          email: signerEmails[s.id]?.trim() || s.email,
        }))
      );
      setSignerEmails({});
    } catch (err) {
      console.error('Error al guardar correos:', err);
      setEmailSaveError('No se pudieron guardar los correos. Intenta nuevamente.');
    } finally {
      setSavingEmails(false);
    }
  };

  const allDocsApproved = !loadingDocs && docs.length > 0 && docs.every((d) => d.status === 'approved');
  const allSignersHaveEmail = signers.length > 0 && signers.every((s) => s.email);

  return (
    <div className="mx-auto w-full max-w-2xl bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden min-h-[600px]">
      <div className="p-6 md:p-8">
        <div className="mb-8 border-b border-slate-100 dark:border-slate-800 pb-6">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Firma de Pagaré</h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Gestiona los documentos legales necesarios para habilitar tu línea de crédito.
          </p>
        </div>

        {!loadingDocs && (
          <div className="mb-8">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Tipo de negocio</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {([
                { type: 'empresa' as const, icon: 'business', label: 'Empresa', desc: 'Persona jurídica (requiere escritura y poderes)' },
                { type: 'persona_natural' as const, icon: 'person', label: 'Persona Natural', desc: 'Requiere RUT / Carnet de identidad (ambos lados)' },
              ]).map((opt) => {
                const selected = companyType === opt.type;
                const disabled = hasUploadedDocs || savingType;
                return (
                  <button
                    key={opt.type}
                    type="button"
                    disabled={disabled && !selected}
                    onClick={() => handleSelectType(opt.type)}
                    className={`relative flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all ${
                      selected
                        ? 'border-primary bg-primary/5 dark:bg-primary/10 ring-1 ring-primary/20'
                        : disabled
                          ? 'border-slate-200 dark:border-slate-700 opacity-50 cursor-not-allowed'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 cursor-pointer'
                    }`}
                  >
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      selected ? 'bg-primary/10 dark:bg-primary/20' : 'bg-slate-100 dark:bg-slate-800'
                    }`}>
                      <span className={`material-symbols-outlined text-xl ${selected ? 'text-primary' : 'text-slate-400'}`}>
                        {opt.icon}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold ${selected ? 'text-primary' : 'text-slate-800 dark:text-slate-200'}`}>
                        {opt.label}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{opt.desc}</p>
                    </div>
                    {selected && (
                      <span className="absolute top-3 right-3 material-symbols-outlined text-primary text-lg">check_circle</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-12">
          <section>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
              <span className="bg-primary text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center mr-3 flex-shrink-0">
                1
              </span>
              Validación de documentos
            </h3>

            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
              <div className="space-y-5 mb-6">
                {loadingDocs
                  ? (companyType === 'persona_natural' ? [1] : [1, 2]).map((i) => (
                      <div key={i} className="h-28 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
                    ))
                  : null}
                {!loadingDocs && docs.length === 0 && (
                  <div className="flex items-start gap-3 px-4 py-5 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-700/40">
                    <span className="material-symbols-outlined text-amber-500 text-xl mt-0.5">info</span>
                    <div className="text-sm">
                      <p className="font-medium text-amber-900 dark:text-amber-200">Selecciona primero el tipo de negocio</p>
                      <p className="text-amber-700 dark:text-amber-300 mt-0.5">
                        Elige <strong>Empresa</strong> o <strong>Persona Natural</strong> arriba para que te mostremos los documentos que necesitamos validar.
                      </p>
                    </div>
                  </div>
                )}
                {!loadingDocs && docs.map((doc) => {
                  const canReplace = doc.status !== 'approved';
                  return (
                  <div key={doc.id} className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`material-symbols-outlined text-lg ${doc.status === 'not_uploaded' ? 'text-slate-400' : 'text-primary'}`}>
                          description
                        </span>
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                          {doc.name}
                        </span>
                      </div>
                      <DocumentStatusBadge status={doc.status} />
                    </div>

                    {doc.uploading ? (
                      <div className="flex items-center gap-3 px-3 py-3 bg-blue-50 dark:bg-blue-900/10 rounded border border-blue-200 dark:border-blue-700/40">
                        <span className="material-symbols-outlined text-blue-500 text-base flex-shrink-0 animate-spin">progress_activity</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-blue-800 dark:text-blue-300 truncate">{doc.file?.name}</p>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Subiendo archivo...</p>
                        </div>
                      </div>
                    ) : doc.uploadError ? (
                      <div className="flex items-start gap-3 px-3 py-3 bg-red-50 dark:bg-red-900/10 rounded border border-red-200 dark:border-red-700/40">
                        <span className="material-symbols-outlined text-red-500 text-base flex-shrink-0 mt-0.5">error</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-red-800 dark:text-red-300">Error al subir el archivo</p>
                          <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">{doc.uploadError}</p>
                        </div>
                        <label
                            htmlFor={`retry-${doc.id}`}
                            className="flex-shrink-0 text-xs text-red-700 dark:text-red-400 font-medium cursor-pointer hover:underline"
                          >
                            Reintentar
                            <input
                              id={`retry-${doc.id}`}
                              type="file"
                              className="hidden"
                              accept=".pdf,.docx,.jpg,.jpeg"
                              onChange={(e) => handleFileChange(doc.id, e.target.files?.[0] ?? null)}
                            />
                          </label>
                      </div>
                    ) : doc.fileName && doc.status !== 'not_uploaded' ? (
                      <div className={`flex items-center justify-between px-3 py-2 rounded border ${doc.status === 'approved' ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-700/40' : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-700/40'}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`material-symbols-outlined text-base flex-shrink-0 ${doc.status === 'approved' ? 'text-emerald-500' : 'text-amber-500'}`}>attach_file</span>
                          <span className="text-xs text-slate-700 dark:text-slate-300 truncate">{doc.fileName}</span>
                          {doc.uploadedAt && (
                            <span className="text-xs text-slate-400 flex-shrink-0">
                              · subido el {new Date(doc.uploadedAt).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' })}
                            </span>
                          )}
                        </div>
                        {canReplace && (
                          <label
                            htmlFor={`replace-${doc.id}`}
                            className="ml-3 flex-shrink-0 text-xs text-primary font-medium cursor-pointer hover:underline"
                            title="Reemplazar archivo"
                          >
                            Reemplazar
                            <input
                              id={`replace-${doc.id}`}
                              type="file"
                              className="hidden"
                              accept=".pdf,.docx,.jpg,.jpeg"
                              onChange={(e) => handleFileChange(doc.id, e.target.files?.[0] ?? null)}
                            />
                          </label>
                        )}
                      </div>
                    ) : (
                      <label
                        htmlFor={`dropzone-${doc.id}`}
                        className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors group ${
                          dragOverDocId === doc.id
                            ? 'border-primary bg-primary/5 dark:bg-primary/10'
                            : 'border-slate-300 bg-slate-50 hover:bg-white dark:bg-slate-800/50 dark:hover:bg-slate-800 dark:border-slate-600 hover:border-primary dark:hover:border-primary'
                        }`}
                        onDragOver={(e) => { e.preventDefault(); setDragOverDocId(doc.id); }}
                        onDragLeave={() => setDragOverDocId(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragOverDocId(null);
                          const file = e.dataTransfer.files?.[0];
                          if (file) handleFileChange(doc.id, file);
                        }}
                      >
                        <div className="flex flex-col items-center justify-center gap-1">
                          <span className="material-symbols-outlined text-slate-400 group-hover:text-primary transition-colors">cloud_upload</span>
                          <p className="text-xs text-slate-500 dark:text-slate-400 text-center px-4">
                            <span className="font-semibold text-primary">Haz clic para subir</span> o arrastra y suelta
                          </p>
                          <p className="text-xs text-slate-400">{doc.hint}</p>
                        </div>
                        <input
                          id={`dropzone-${doc.id}`}
                          type="file"
                          className="hidden"
                          accept=".pdf,.docx,.jpg,.jpeg"
                          onChange={(e) => handleFileChange(doc.id, e.target.files?.[0] ?? null)}
                        />
                      </label>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className={!allDocsApproved && !isCompleted ? 'opacity-50 pointer-events-none' : ''}>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
              <span className="bg-primary text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center mr-3 flex-shrink-0">
                2
              </span>
              Firmantes del pagaré
            </h3>

            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                {allDocsApproved || isCompleted
                  ? 'Los firmantes son asignados por administración. Una vez disponibles, asigna el correo electrónico de cada representante legal.'
                  : 'Los documentos deben estar aprobados antes de poder gestionar firmantes.'}
              </p>

              {loadingSigners ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-14 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : signers.length === 0 ? (
                <div className="flex items-start gap-3 px-4 py-3.5 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700/40 rounded-lg">
                  <span className="material-symbols-outlined text-blue-500 flex-shrink-0 mt-0.5" style={{ fontSize: 18 }}>
                    info
                  </span>
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      Firmantes pendientes de asignación
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                      Los firmantes del pagaré serán cargados por el equipo de administración. Recarga esta página para verificar si ya fueron asignados.
                    </p>
                  </div>
                </div>
              ) : allSignersHaveEmail || isCompleted ? (
                <div className="space-y-2">
                  {signers.map((signer) => (
                    <div
                      key={signer.id}
                      className="flex items-center px-4 py-2.5 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                          <span className="material-symbols-outlined text-blue-500 dark:text-blue-400" style={{ fontSize: 16 }}>
                            person
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{signer.name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{signer.email}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {signers.map((signer) => (
                    <div
                      key={signer.id}
                      className="flex items-center gap-4 px-4 py-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700"
                    >
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                          <span className="material-symbols-outlined text-blue-500 dark:text-blue-400" style={{ fontSize: 16 }}>
                            person
                          </span>
                        </div>
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 min-w-[140px]">
                          {signer.name}
                        </p>
                      </div>
                      <div className="flex-1">
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="material-symbols-outlined text-slate-400 text-lg">mail</span>
                          </div>
                          <input
                            type="email"
                            value={signerEmails[signer.id] ?? signer.email ?? ''}
                            onChange={(e) => {
                              setSignerEmails((prev) => ({ ...prev, [signer.id]: e.target.value }));
                              setEmailSaveError(null);
                            }}
                            placeholder="correo@empresa.com"
                            className="block w-full pl-10 py-2 text-sm border border-slate-300 rounded-md focus:ring-primary focus:border-primary dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-slate-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  {emailSaveError && (
                    <p className="text-xs text-red-500">{emailSaveError}</p>
                  )}

                  <button
                    type="button"
                    onClick={handleSaveEmails}
                    disabled={savingEmails}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {savingEmails ? (
                      <>
                        <span className="material-symbols-outlined text-base animate-spin">progress_activity</span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-base">save</span>
                        Guardar correos
                      </>
                    )}
                  </button>
                </div>
              )}

              {isCompleted && (
                <div className="flex items-start gap-3 px-4 py-3.5 mt-4 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-700/40 rounded-lg">
                  <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" style={{ fontSize: 18 }}>
                    lock
                  </span>
                  <div>
                    <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                      El pagaré ya fue firmado
                    </p>
                    <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-0.5">
                      No es posible modificar los firmantes.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section>
            <h3 className={`text-lg font-semibold mb-4 flex items-center ${isCompleted ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-500'}`}>
              <span className={`text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center mr-3 flex-shrink-0 ${isCompleted ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}>
                3
              </span>
              Recepción de documento firmado
            </h3>

            <div className={`rounded-lg p-8 border flex flex-col items-center text-center ${isCompleted ? 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700' : 'bg-slate-50/50 dark:bg-slate-800/30 border-slate-200 dark:border-slate-700'}`}>
              <div className="flex flex-col items-center">
                <div className={`h-16 w-16 rounded-lg shadow-sm border flex items-center justify-center mb-6 ${isCompleted ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'}`}>
                  <span className={`material-symbols-outlined text-4xl ${isCompleted ? 'text-primary' : 'text-slate-300 dark:text-slate-600'}`}>
                    picture_as_pdf
                  </span>
                </div>

                {isCompleted ? (
                  <>
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200 px-4 py-3 rounded-md text-sm mb-6 flex items-start gap-3 text-left">
                      <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5">
                        check_circle
                      </span>
                      <p>
                        Tu pagaré fue firmado exitosamente. Puedes descargar el documento a continuación para tus registros.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleDownload}
                      disabled={downloadingDoc}
                      className="bg-primary hover:bg-primary/90 text-white px-6 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed w-full justify-center sm:w-auto"
                    >
                      {downloadingDoc ? (
                        <>
                          <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                          Descargando...
                        </>
                      ) : (
                        <>
                          <span className="material-symbols-outlined text-sm">download</span>
                          Descargar Pagaré Firmado
                        </>
                      )}
                    </button>
                    {downloadError && (
                      <p className="text-sm text-red-500 mt-3">{downloadError}</p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 px-4 py-3 rounded-md text-sm mb-6 flex items-start gap-3 text-left">
                      <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 shrink-0 mt-0.5">
                        info
                      </span>
                      <p>
                        El proceso de firma se realiza fuera de FLUX. Al finalizar, el documento firmado se
                        cargará automáticamente en esta sección para tu respaldo.
                      </p>
                    </div>

                    <button
                      disabled
                      className="bg-slate-200 text-slate-400 dark:bg-slate-700 dark:text-slate-500 px-6 py-2 rounded-lg font-medium text-sm flex items-center gap-2 cursor-not-allowed w-full justify-center sm:w-auto"
                    >
                      <span className="material-symbols-outlined text-sm">download</span>
                      Descargar Pagaré Firmado
                    </button>

                    <p className="text-xs text-slate-400 mt-3">Estado: Esperando firma externa...</p>
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
